import React, { useState } from "react";
import {
  Container,
  Card,
  Form,
  Button,
  Alert,
  ButtonGroup,
  Row,
  Col,
} from "react-bootstrap";
import { supabase } from "../../config/Supabase";

const initialCommon = {
  contact_name: "",
  contact_email: "",
  contact_phone: "",
  country: "",
  reason_for_interest: "",
  expected_outcomes: "",
  marketing_approach: "",
  legal_agreement: false,
};

const initialBusiness = {
  company_name: "",
  website_url: "",
  industry: "",
  company_size: "",
};

const initialCreator = {
  creator_handle: "",
  primary_platform: "",
  social_handles: {
    instagram: "",
    tiktok: "",
    youtube: "",
    twitter: "",
    podcast: "",
    blog: "",
  },
  audience_size_range: "",
  content_niche: "",
  audience_demographic: "",
};

const PAYOUT_METHODS = [
  { value: "paypal", label: "PayPal" },
  { value: "venmo", label: "Venmo" },
  { value: "zelle", label: "Zelle" },
  { value: "ach", label: "US Bank (ACH)" },
  { value: "stripe_connect", label: "Stripe Connect" },
];

const AffiliateCard = () => {
  const [applicantType, setApplicantType] = useState("creator");
  const [common, setCommon] = useState(initialCommon);
  const [business, setBusiness] = useState(initialBusiness);
  const [creator, setCreator] = useState(initialCreator);
  const [payoutMethod, setPayoutMethod] = useState("");
  const [payoutDetails, setPayoutDetails] = useState({});

  const [error, setError] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const setPayoutDetail = (key, value) =>
    setPayoutDetails((prev) => ({ ...prev, [key]: value }));

  const handlePayoutMethodChange = (val) => {
    setPayoutMethod(val);
    setPayoutDetails({});
  };

  const handleCommonChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCommon((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleBusinessChange = (e) => {
    const { name, value } = e.target;
    setBusiness((prev) => ({ ...prev, [name]: value }));
  };

  const handleCreatorChange = (e) => {
    const { name, value } = e.target;
    setCreator((prev) => ({ ...prev, [name]: value }));
  };

  const handleSocialChange = (platform, value) => {
    setCreator((prev) => ({
      ...prev,
      social_handles: { ...prev.social_handles, [platform]: value },
    }));
  };

  const validateEmail = (email) => /\S+@\S+\.\S+/.test(email);

  const preprocessURL = (url) => {
    if (!url) return url;
    if (!/^(https?:\/\/|ftp:\/\/)/i.test(url)) return `http://${url}`;
    return url;
  };

  const validateURL = (url) => {
    const pattern =
      /^(https?:\/\/|ftp:\/\/)([\da-z.-]+)\.([a-z]{2,})\/?([\/\w .-]*)*\/?$/;
    return pattern.test(url);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsSubmitted(false);

    if (!validateEmail(common.contact_email)) {
      setError("Please enter a valid email address.");
      return;
    }
    if (!common.legal_agreement) {
      setError("You must agree to the terms and privacy policy.");
      return;
    }
    if (
      !common.contact_name ||
      !common.reason_for_interest ||
      !common.expected_outcomes ||
      !common.marketing_approach
    ) {
      setError("Please fill in all required fields.");
      return;
    }

    let payload = {
      applicant_type: applicantType,
      contact_name: common.contact_name,
      contact_email: common.contact_email,
      contact_phone: common.contact_phone || null,
      country: common.country || null,
      reason_for_interest: common.reason_for_interest,
      expected_outcomes: common.expected_outcomes,
      marketing_approach: common.marketing_approach,
      legal_agreement: common.legal_agreement,
    };

    if (applicantType === "business") {
      const processedURL = preprocessURL(business.website_url);
      if (!validateURL(processedURL)) {
        setError("Please enter a valid website URL.");
        return;
      }
      if (
        !business.company_name ||
        !business.website_url ||
        !business.industry ||
        !business.company_size
      ) {
        setError("Please fill in all required business fields.");
        return;
      }
      payload = {
        ...payload,
        company_name: business.company_name,
        website_url: processedURL,
        industry: business.industry,
        company_size: business.company_size,
      };
    } else {
      if (
        !creator.creator_handle ||
        !creator.primary_platform ||
        !creator.audience_size_range ||
        !creator.content_niche
      ) {
        setError("Please fill in all required creator fields.");
        return;
      }
      payload = {
        ...payload,
        creator_handle: creator.creator_handle,
        primary_platform: creator.primary_platform,
        social_handles: creator.social_handles,
        audience_size_range: creator.audience_size_range,
        content_niche: creator.content_niche,
        audience_demographic: creator.audience_demographic || null,
      };
    }

    try {
      setSubmitting(true);
      const { error: insertError } = await supabase
        .from("affiliate_applications")
        .insert(payload);

      if (insertError) throw insertError;

      setIsSubmitted(true);
      setCommon(initialCommon);
      setBusiness(initialBusiness);
      setCreator(initialCreator);
      setPayoutMethod("");
      setPayoutDetails({});
    } catch (err) {
      console.error("Error submitting affiliate application:", err);
      setError("Failed to submit the request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container className="mt-5">
      <Card
        className="p-4 shadow"
        style={{ borderRadius: "5px", border: "none" }}
      >
        <ButtonGroup className="mb-4 w-100">
          <Button
            variant={applicantType === "creator" ? "primary" : "outline-primary"}
            onClick={() => setApplicantType("creator")}
            type="button"
          >
            I'm a Content Creator
          </Button>
          <Button
            variant={applicantType === "business" ? "primary" : "outline-primary"}
            onClick={() => setApplicantType("business")}
            type="button"
          >
            I'm a Business
          </Button>
        </ButtonGroup>

        <Form onSubmit={handleSubmit}>
          {applicantType === "creator" ? (
            <>
              <Form.Group className="mb-3">
                <Form.Control
                  type="text"
                  name="creator_handle"
                  placeholder="Creator name / handle*"
                  value={creator.creator_handle}
                  onChange={handleCreatorChange}
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Select
                  name="primary_platform"
                  value={creator.primary_platform}
                  onChange={handleCreatorChange}
                  required
                >
                  <option value="">Primary platform *</option>
                  <option value="instagram">Instagram</option>
                  <option value="tiktok">TikTok</option>
                  <option value="youtube">YouTube</option>
                  <option value="podcast">Podcast</option>
                  <option value="blog">Blog / Newsletter</option>
                  <option value="twitter">X / Twitter</option>
                  <option value="other">Other</option>
                </Form.Select>
              </Form.Group>

              <p
                style={{
                  fontSize: "13px",
                  color: "#666",
                  marginBottom: "8px",
                }}
              >
                Share any handles you'd like us to know about (optional):
              </p>
              <Row>
                {[
                  ["instagram", "Instagram @"],
                  ["tiktok", "TikTok @"],
                  ["youtube", "YouTube channel"],
                  ["twitter", "X / Twitter @"],
                  ["podcast", "Podcast name"],
                  ["blog", "Blog / website URL"],
                ].map(([key, placeholder]) => (
                  <Col xs={12} md={6} key={key} className="mb-3">
                    <Form.Control
                      type="text"
                      placeholder={placeholder}
                      value={creator.social_handles[key] || ""}
                      onChange={(e) => handleSocialChange(key, e.target.value)}
                    />
                  </Col>
                ))}
              </Row>

              <Form.Group className="mb-3">
                <Form.Select
                  name="audience_size_range"
                  value={creator.audience_size_range}
                  onChange={handleCreatorChange}
                  required
                >
                  <option value="">Total audience size *</option>
                  <option value="under-1k">Under 1,000</option>
                  <option value="1k-10k">1,000 - 10,000</option>
                  <option value="10k-50k">10,000 - 50,000</option>
                  <option value="50k-100k">50,000 - 100,000</option>
                  <option value="100k-500k">100,000 - 500,000</option>
                  <option value="500k-1m">500,000 - 1,000,000</option>
                  <option value="1m+">1,000,000+</option>
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Control
                  type="text"
                  name="content_niche"
                  placeholder="Content niche (e.g. holistic health, yoga, ayurveda)*"
                  value={creator.content_niche}
                  onChange={handleCreatorChange}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Control
                  as="textarea"
                  rows={2}
                  name="audience_demographic"
                  placeholder="Tell us about your audience (age range, gender mix, geography, interests)"
                  value={creator.audience_demographic}
                  onChange={handleCreatorChange}
                />
              </Form.Group>
            </>
          ) : (
            <>
              <Form.Group className="mb-3">
                <Form.Control
                  type="text"
                  name="company_name"
                  placeholder="Company Name*"
                  value={business.company_name}
                  onChange={handleBusinessChange}
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Control
                  type="text"
                  name="website_url"
                  placeholder="Website URL*"
                  value={business.website_url}
                  onChange={handleBusinessChange}
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Select
                  name="industry"
                  value={business.industry}
                  onChange={handleBusinessChange}
                  required
                >
                  <option value="">Select Industry *</option>
                  <option value="Healthcare">Healthcare</option>
                  <option value="Wellness">Wellness</option>
                  <option value="Sports and Recreation">
                    Sports and Recreation
                  </option>
                  <option value="Insurance">Insurance</option>
                  <option value="Education">Education</option>
                  <option value="Retail">Retail</option>
                  <option value="Beauty and Personal Care">
                    Beauty and Personal Care
                  </option>
                  <option value="Mental Health Services">
                    Mental Health Services
                  </option>
                  <option value="Nutrition and Food Services">
                    Nutrition and Food Services
                  </option>
                  <option value="Environmental and Sustainability Initiatives">
                    Environmental and Sustainability Initiatives
                  </option>
                  <option value="Other">Other</option>
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Select
                  name="company_size"
                  value={business.company_size}
                  onChange={handleBusinessChange}
                  required
                >
                  <option value="">Company Size *</option>
                  <option value="1-100">1-100 Members</option>
                  <option value="101-500">100-500 Members</option>
                  <option value="501-1000">500-1000 Members</option>
                  <option value="1000-2500">1000-2500 Members</option>
                  <option value="2500-5000">2500-5000 Members</option>
                  <option value="5000-10000">5000-10,000 Members</option>
                  <option value="10000-50000">10,000-50,000 Members</option>
                  <option value="50000-100000">50,000-100,000 Members</option>
                  <option value="100000-500000">100,000-500,000 Members</option>
                  <option value="500000-1000000">
                    500,000-1,000,000 Members
                  </option>
                </Form.Select>
              </Form.Group>
            </>
          )}

          <Form.Group className="mb-3">
            <Form.Control
              type="text"
              name="contact_name"
              placeholder="Contact Name*"
              value={common.contact_name}
              onChange={handleCommonChange}
              required
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Control
              type="email"
              name="contact_email"
              placeholder="Contact Email*"
              value={common.contact_email}
              onChange={handleCommonChange}
              required
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Control
              type="tel"
              name="contact_phone"
              placeholder="Contact Phone Number"
              value={common.contact_phone}
              onChange={handleCommonChange}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Control
              type="text"
              name="country"
              placeholder="Country"
              value={common.country}
              onChange={handleCommonChange}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Control
              as="textarea"
              rows={3}
              name="reason_for_interest"
              placeholder="Why do you want to partner with us?*"
              value={common.reason_for_interest}
              onChange={handleCommonChange}
              required
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Control
              as="textarea"
              rows={3}
              name="expected_outcomes"
              placeholder="What outcomes are you hoping for?*"
              value={common.expected_outcomes}
              onChange={handleCommonChange}
              required
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Control
              as="textarea"
              rows={3}
              name="marketing_approach"
              placeholder={
                applicantType === "creator"
                  ? "How do you plan to promote Sarv to your audience?*"
                  : "Marketing Approach*"
              }
              value={common.marketing_approach}
              onChange={handleCommonChange}
              required
            />
          </Form.Group>
          {/* Payout preference */}
          <div
            style={{
              borderTop: "1px solid #eee",
              marginTop: "8px",
              paddingTop: "16px",
              marginBottom: "4px",
              fontSize: "14px",
              fontWeight: 600,
              color: "#444",
            }}
          >
            Preferred Payout Method
          </div>
          <p style={{ fontSize: "12px", color: "#888", marginBottom: "12px" }}>
            How would you like to receive your commissions? You can update this
            later from your dashboard.
          </p>
          <Form.Group className="mb-3">
            <Form.Select
              value={payoutMethod}
              onChange={(e) => handlePayoutMethodChange(e.target.value)}
            >
              <option value="">Select a payout method (optional)</option>
              {PAYOUT_METHODS.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          {payoutMethod === "paypal" && (
            <Form.Group className="mb-3">
              <Form.Control
                type="email"
                placeholder="PayPal email *"
                value={payoutDetails.paypal_email || ""}
                onChange={(e) => setPayoutDetail("paypal_email", e.target.value)}
                required
              />
            </Form.Group>
          )}

          {payoutMethod === "venmo" && (
            <Form.Group className="mb-3">
              <Form.Control
                type="text"
                placeholder="Venmo username or phone number *"
                value={payoutDetails.venmo_handle || ""}
                onChange={(e) => setPayoutDetail("venmo_handle", e.target.value)}
                required
              />
            </Form.Group>
          )}

          {payoutMethod === "zelle" && (
            <Form.Group className="mb-3">
              <Form.Control
                type="text"
                placeholder="Zelle email or phone number *"
                value={payoutDetails.zelle_contact || ""}
                onChange={(e) =>
                  setPayoutDetail("zelle_contact", e.target.value)
                }
                required
              />
            </Form.Group>
          )}

          {payoutMethod === "ach" && (
            <Row>
              <Col xs={12} md={6} className="mb-3">
                <Form.Control
                  type="text"
                  placeholder="Bank name *"
                  value={payoutDetails.bank_name || ""}
                  onChange={(e) => setPayoutDetail("bank_name", e.target.value)}
                  required
                />
              </Col>
              <Col xs={12} md={6} className="mb-3">
                <Form.Control
                  type="text"
                  placeholder="Account holder name *"
                  value={payoutDetails.account_holder || ""}
                  onChange={(e) =>
                    setPayoutDetail("account_holder", e.target.value)
                  }
                  required
                />
              </Col>
              <Col xs={12} md={6} className="mb-3">
                <Form.Control
                  type="text"
                  placeholder="Account number *"
                  value={payoutDetails.account_number || ""}
                  onChange={(e) =>
                    setPayoutDetail("account_number", e.target.value)
                  }
                  required
                />
              </Col>
              <Col xs={12} md={6} className="mb-3">
                <Form.Control
                  type="text"
                  placeholder="Routing number (9 digits) *"
                  value={payoutDetails.routing_number || ""}
                  onChange={(e) =>
                    setPayoutDetail("routing_number", e.target.value)
                  }
                  required
                  maxLength={9}
                />
              </Col>
            </Row>
          )}

          {payoutMethod === "stripe_connect" && (
            <Form.Group className="mb-3">
              <Form.Control
                type="email"
                placeholder="Stripe account email *"
                value={payoutDetails.stripe_email || ""}
                onChange={(e) =>
                  setPayoutDetail("stripe_email", e.target.value)
                }
                required
              />
            </Form.Group>
          )}

          <Form.Group className="mb-3" id="agreement-checkbox">
            <Form.Check
              type="checkbox"
              label="I agree to the platform's affiliate program terms and conditions and consent to the privacy policy."
              name="legal_agreement"
              checked={common.legal_agreement}
              onChange={handleCommonChange}
              required
            />
          </Form.Group>

          <Button
            variant="primary"
            type="submit"
            className="business-button"
            disabled={submitting}
          >
            {submitting ? "Submitting..." : "Request to Join"}
          </Button>
        </Form>
        {error && (
          <Alert variant="danger" className="mt-3">
            {error}
          </Alert>
        )}
        {isSubmitted && (
          <Alert variant="success" className="mt-3">
            Your affiliate application has been submitted successfully! We'll
            review it and reach out by email with next steps.
          </Alert>
        )}
      </Card>
    </Container>
  );
};

export default AffiliateCard;
